import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { trackPageView } from '../services/gameAnalytics'
import { formatSecondsTaken } from '../utils/timeUtils'
import { SaveProgressPrompt } from '../components/SaveProgressPrompt'
import { StreakCard } from '../components/StreakCard'
import { useStats } from '../contexts/StatsContext'

export const AnagramCompletionPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // The game that just finished is already recorded on the server; ask again so the streak is current.
  const { refresh } = useStats()
  useEffect(() => {
    void refresh()
  }, [refresh])
  const state = location.state || {}
  const {
    won = false,
    targetWord = '',
    definition = '',
    setterNote = null,
    attemptCount = 0,
    secondsTaken = null,
  } = state

  useEffect(() => {
    if (!location.state) {
      navigate('/anagram')
      return
    }
    try {
      trackPageView(location.pathname, {
        result: won ? 'won' : 'active',
        attempt_count: attemptCount,
      })
    } catch (error) {
      console.error('Failed to track anagram completion page view:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBackToModes = () => navigate('/anagram')

  return (
    <div className="mode-selection" data-hj-allow="true">
      <motion.h1
        className="mode-title"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {won ? 'Magnificent!' : 'Good Try!'}
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="mb-8 max-w-xl mx-auto text-center"
      >
        {targetWord && (
          <>
            <p className="text-2xl font-bold text-white/80 mb-2">The word was:</p>
            <p className="text-4xl md:text-5xl font-black uppercase tracking-widest inline-block px-6 py-2">
              {targetWord.toUpperCase()}
            </p>
          </>
        )}

        {definition && (
          <p className="text-base text-white/60 max-w-md mx-auto mt-2">Meaning: {definition}</p>
        )}

        <p className="text-white/50 text-sm mt-4">
          {won ? 'Solved' : 'Played'} in {attemptCount} attempt{attemptCount === 1 ? '' : 's'}
          {secondsTaken !== null && ` in ${formatSecondsTaken(secondsTaken)}`}
        </p>

        {setterNote && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
            className="mt-8 p-6 bg-zinc-950/80 border border-zinc-900 rounded-3xl max-w-sm mx-auto text-left shadow-2xl"
          >
            <div className="border-b border-zinc-900 pb-3 mb-3">
              <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest block">
                Daily Setter
              </span>
              <span className="text-sm font-extrabold text-violet-400">{setterNote.penName}</span>
            </div>
            <p className="text-zinc-300 text-sm italic font-serif leading-relaxed pl-3 border-l-2 border-violet-500/50">
              "{setterNote.note}"
            </p>
          </motion.div>
        )}
      </motion.div>

      <div className="mb-4 mx-auto w-full max-w-md">
        <StreakCard />
      </div>

      <div className="mx-auto flex justify-center">
        <SaveProgressPrompt won={won} />
      </div>

      <button onClick={handleBackToModes} className="play-button">
        Back to Anagram Modes
      </button>
    </div>
  )
}
